import type { WsConnectionEvent } from '@atlassian/graphql-subscription/utils/client';
import { getAnalyticsWebClientPromise } from '@jira/platform__observability-product-analytics-web-client-async';

const CYCLE_SAMPLE_RATE = 0.1;
const DETAIL_EVENT_LIMIT = 20;
// Leaves the last slot for a recovery.
const DETAIL_FAILURE_LIMIT = DETAIL_EVENT_LIMIT - 1;
// Longer than the ack timeout, so a timed-out handshake is not also reported as stalled.
const STALL_TIMEOUT_MS = 30_000;
// Longer than one capped retry wait, so one slow attempt alone is not a block.
const BLOCKED_AFTER_MS = 30_000;

const CLOSE_CLASSES: Partial<Record<number, string>> = {
	1000: 'normal',
	1001: 'going-away',
	1005: 'no-status',
	1006: 'abnormal',
	1011: 'server-error',
	1012: 'server-restart',
	1013: 'server-overloaded',
	1014: 'server-error',
	4004: 'protocol-error',
	4005: 'protocol-error',
	4400: 'protocol-error',
	4401: 'auth',
	4403: 'auth',
	4406: 'protocol-error',
	4408: 'timeout',
	4409: 'protocol-error',
	4429: 'protocol-error',
	4430: 'rate-limited',
	4499: 'terminated',
	4500: 'server-error',
	4504: 'timeout',
};

const getCloseClass = (closeCode: number | null): string => {
	if (closeCode === null) {
		return 'unknown';
	}
	const closeClass = CLOSE_CLASSES[closeCode];
	if (closeClass) {
		return closeClass;
	}
	// graphql-ws does not retry any other 1xxx code.
	return closeCode >= 1000 && closeCode <= 1999 ? 'fatal' : 'other';
};

const getFailedAttemptsBucket = (failedAttempts: number): string => {
	if (failedAttempts === 0) {
		return 'none';
	}
	if (failedAttempts === 1) {
		return '1';
	}
	return failedAttempts <= 4 ? '2-4' : '5+';
};

const getOutageBucket = (streakDurationMs: number | null): string => {
	if (streakDurationMs === null) {
		return 'none';
	}
	if (streakDurationMs < 10_000) {
		return '<10s';
	}
	if (streakDurationMs < 60_000) {
		return '10s-1m';
	}
	return streakDurationMs < 600_000 ? '1-10m' : '>10m';
};

type SocketError = {
	kind: Extract<WsConnectionEvent, { type: 'error' }>['kind'];
	// `socket-event` is a browser socket error, which carries no detail.
	type: 'exception' | 'socket-event';
};

type SocketState = {
	attempt: number;
	connectingAt: number;
	sampled: boolean;
	opened: boolean;
	connected: boolean;
	stalled: boolean;
	// Until the socket connects, the handshake runs until its close.
	handshakeMs?: number;
	// Later errors are usually effects of the first.
	firstError: SocketError | null;
	cancelStall?: () => void;
};

type Attributes = Record<string, string | number | boolean | null>;

type SocketAction = 'connected' | 'closed' | 'stalled' | 'blocked';

const getSocketContextClass = (
	deviceOnline: boolean | null,
	pageVisibility: string | null,
): string => {
	if (deviceOnline === false) {
		return 'offline';
	}
	return pageVisibility === 'hidden' ? 'hidden' : 'normal';
};

const getSocketContext = (): Attributes => {
	const deviceOnline = globalThis.navigator?.onLine ?? null;
	const pageVisibility = globalThis.document?.visibilityState ?? null;
	return {
		deviceOnline,
		pageVisibility,
		socketContext: getSocketContextClass(deviceOnline, pageVisibility),
	};
};

const getOutcome = ({ connected, opened }: SocketState, healthy: boolean): string => {
	if (connected) {
		return healthy ? 'healthy' : 'dropped';
	}
	return opened ? 'refused' : 'unreachable';
};

// Error messages and close reasons are not sent: they can contain consumer or server text.
const getErrorAttributes = ({ firstError }: SocketState): Attributes => ({
	errorType: firstError?.type ?? null,
	errorKind: firstError?.kind ?? null,
});

const sendSocketEvent = (action: SocketAction, attributes: Attributes): void => {
	getAnalyticsWebClientPromise().then((client) => {
		const analyticsClient = client.getInstance();
		if (!analyticsClient) {
			return;
		}

		analyticsClient.sendOperationalEvent({
			action,
			actionSubject: 'relaySubscriptionSocket',
			attributes,
			source: 'jiraRelayEnvironment',
		});
	});
};

/**
 * Reports one `closed` per socket and one `connected` per connection. A random sample of sockets
 * is reported in full; for the rest, only failures, recoveries and stalls are sent, capped per page.
 * `blocked` is sent once for a page that has not connected 30s after its first failure.
 */
export const createConnectionEventReporter = (): ((event: WsConnectionEvent) => void) => {
	// Oldest first: a socket can start before the previous one has closed, so a close belongs to the
	// oldest socket and every other event to the newest.
	const sockets: SocketState[] = [];
	let connectionCount = 0;
	let failureStreak = 0;
	let streakStartedAt: number | null = null;
	let detailEventsSent = 0;
	let lastFailureClass = 'none';
	let cancelBlocked: (() => void) | undefined;
	let blockedSent = false;
	let recoveringFromBlocked = false;

	const noteFailure = (failureClass: string) => {
		lastFailureClass = failureClass;
		if (connectionCount > 0 || blockedSent || cancelBlocked) {
			return;
		}
		const blockedTimer = setTimeout(() => {
			cancelBlocked = undefined;
			blockedSent = true;
			recoveringFromBlocked = true;
			sendSocketEvent('blocked', {
				closeClass: lastFailureClass,
				failureStreak,
				...getSocketContext(),
			});
		}, BLOCKED_AFTER_MS);
		cancelBlocked = () => clearTimeout(blockedTimer);
	};

	const send = (
		socket: SocketState,
		action: SocketAction,
		attributes: Attributes,
		detailLimit: number,
	) => {
		if (socket.sampled) {
			sendSocketEvent(action, {
				...attributes,
				stream: 'rate',
				sampleRate: CYCLE_SAMPLE_RATE,
				detailIndex: null,
			});
			return;
		}
		if (detailEventsSent >= detailLimit) {
			return;
		}
		detailEventsSent += 1;
		sendSocketEvent(action, {
			...attributes,
			stream: 'detail',
			sampleRate: null,
			detailIndex: detailEventsSent,
		});
	};

	return (event) => {
		const newest = sockets.at(-1);
		switch (event.type) {
			case 'connecting': {
				const socket: SocketState = {
					attempt: event.attempt,
					connectingAt: Date.now(),
					sampled: Math.random() < CYCLE_SAMPLE_RATE,
					opened: false,
					connected: false,
					stalled: false,
					firstError: null,
				};
				const stallTimer = setTimeout(() => {
					socket.stalled = true;
					streakStartedAt ??= Date.now();
					send(
						socket,
						'stalled',
						{
							opened: socket.opened,
							attempt: socket.attempt,
							...getErrorAttributes(socket),
							connectionCount,
							failureStreak,
							...getSocketContext(),
						},
						DETAIL_FAILURE_LIMIT,
					);
					noteFailure('stalled');
				}, STALL_TIMEOUT_MS);
				socket.cancelStall = () => clearTimeout(stallTimer);
				sockets.push(socket);
				return;
			}
			case 'opened':
				if (newest) {
					newest.opened = true;
				}
				return;
			case 'error':
				if (newest) {
					newest.firstError ??= {
						kind: event.kind,
						type: event.message === null ? 'socket-event' : 'exception',
					};
				}
				return;
			case 'connected':
				if (!newest) {
					return;
				}
				newest.connected = true;
				newest.handshakeMs = Date.now() - newest.connectingAt;
				newest.cancelStall?.();
				cancelBlocked?.();
				cancelBlocked = undefined;
				connectionCount = event.connectionCount;
				if (newest.sampled || failureStreak > 0 || recoveringFromBlocked || newest.stalled) {
					const streakDurationMs = streakStartedAt === null ? null : Date.now() - streakStartedAt;
					send(
						newest,
						'connected',
						{
							connectionCount: event.connectionCount,
							failedAttempts: failureStreak,
							failedAttemptsBucket: getFailedAttemptsBucket(failureStreak),
							streakDurationMs,
							outageBucket: getOutageBucket(streakDurationMs),
							afterBlocked: recoveringFromBlocked,
							afterStalled: newest.stalled,
						},
						DETAIL_EVENT_LIMIT,
					);
				}
				failureStreak = 0;
				streakStartedAt = null;
				recoveringFromBlocked = false;
				return;
			case 'closed': {
				const socket = sockets.shift();
				// `terminate()` during a retry wait emits a close with no socket.
				if (!socket) {
					return;
				}
				socket.cancelStall?.();
				const healthy = socket.connected && event.closeCode === 1000 && event.wasClean === true;
				// A newer connected socket has already ended the outage.
				const outageEnded = sockets.some(({ connected }) => connected);
				if (!healthy && !outageEnded) {
					failureStreak += 1;
					streakStartedAt ??= Date.now();
					noteFailure(getCloseClass(event.closeCode));
				}
				if (socket.sampled || !healthy) {
					send(
						socket,
						'closed',
						{
							outcome: getOutcome(socket, healthy),
							closeClass: getCloseClass(event.closeCode),
							connectedForMs: event.connectedForMs,
							handshakeMs: socket.handshakeMs ?? Date.now() - socket.connectingAt,
							closeCode: event.closeCode,
							wasClean: event.wasClean,
							...getErrorAttributes(socket),
							connectionCount,
							failureStreak,
							...getSocketContext(),
						},
						DETAIL_FAILURE_LIMIT,
					);
				}
				return;
			}
			default: {
				const unhandled: never = event;
				return unhandled;
			}
		}
	};
};
